"use client";

import React, { useRef, useEffect } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Download, Award, Users, Coffee, Code, Palette, Zap } from 'lucide-react';
import { RESUME_URL } from '@/lib/site';

gsap.registerPlugin(ScrollTrigger);

const AboutPage: React.FC = () => {
  const heroRef = useRef<HTMLDivElement>(null);
  const statsRef = useRef<HTMLDivElement>(null);
  const skillsRef = useRef<HTMLDivElement>(null);
  const experienceRef = useRef<HTMLDivElement>(null);

  const stats = [
    { icon: Award, label: 'Awards Won', value: '12+' },
    { icon: Users, label: 'Happy Clients', value: '50+' },
    { icon: Coffee, label: 'Cups of Coffee', value: '1,000+' },
    { icon: Code, label: 'Projects Completed', value: '100+' },
  ];

  const skills = [
    { category: 'Frontend', items: ['React', 'Next.js', 'TypeScript', 'GSAP', 'Three.js'] },
    { category: 'Design', items: ['Figma', 'Adobe Suite', 'Webflow', 'Framer', 'Principle'] },
    { category: 'Backend', items: ['Node.js', 'Python', 'PostgreSQL', 'MongoDB', 'GraphQL'] },
    { category: 'Tools', items: ['Git', 'Docker', 'AWS', 'Vercel', 'Linear'] },
  ];

  const experience = [
    {
      role: 'Senior Creative Developer',
      company: 'Digital Agency',
      period: '2022 - Present',
      description: 'Leading creative development for award-winning digital experiences and interactive installations.',
    },
    {
      role: 'Full Stack Developer',
      company: 'Tech Startup',
      period: '2020 - 2022',
      description: 'Built scalable web applications and designed user experiences for B2B SaaS platform.',
    },
    {
      role: 'Frontend Developer',
      company: 'Creative Studio',
      period: '2019 - 2020',
      description: 'Developed interactive websites and animations for luxury brands and cultural institutions.',
    },
  ];

  useEffect(() => {
    const ctx = gsap.context(() => {
      // Hero animation
      gsap.fromTo(
        heroRef.current,
        { opacity: 0, y: 100 },
        { opacity: 1, y: 0, duration: 1, ease: "power2.out" }
      );

      // Stats animation
      const statItems = statsRef.current?.querySelectorAll('.stat-item');
      if (statItems) {
        gsap.fromTo(
          statItems,
          { opacity: 0, scale: 0.8 },
          {
            opacity: 1,
            scale: 1,
            duration: 0.6,
            stagger: 0.1,
            ease: "back.out(1.7)",
            scrollTrigger: {
              trigger: statsRef.current,
              start: "top 80%",
              toggleActions: "play none none reverse",
            },
          }
        );
      }

      // Skills animation
      const skillCategories = skillsRef.current?.querySelectorAll('.skill-category');
      if (skillCategories) {
        skillCategories.forEach((category, index) => {
          gsap.fromTo(
            category,
            { opacity: 0, y: 60 },
            {
              opacity: 1,
              y: 0,
              duration: 0.8,
              delay: index * 0.1,
              ease: "power2.out",
              scrollTrigger: {
                trigger: category,
                start: "top 85%",
                toggleActions: "play none none reverse",
              },
            }
          );
        });
      }

      // Experience animation
      const experienceItems = experienceRef.current?.querySelectorAll('.experience-item');
      if (experienceItems) {
        experienceItems.forEach((item, index) => {
          gsap.fromTo(
            item,
            { opacity: 0, x: index % 2 === 0 ? -60 : 60 },
            {
              opacity: 1,
              x: 0,
              duration: 0.8,
              ease: "power2.out",
              scrollTrigger: {
                trigger: item,
                start: "top 85%",
                toggleActions: "play none none reverse",
              },
            }
          );
        });
      }
    });

    return () => ctx.revert();
  }, []);

  return (
    <div className="bg-background text-foreground">
      {/* Hero Section */}
      <section ref={heroRef} className="py-24">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <div>
                <h1 className="text-6xl md:text-8xl font-bold mb-8 leading-tight">
                  About
                  <br />
                  <span className="text-accent">Me</span>
                </h1>
                <p className="text-xl md:text-2xl text-gray-600 leading-relaxed mb-8">
                  I&apos;m a creative developer who bridges the gap between design and technology, 
                  crafting digital experiences that inspire and engage.
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <a href={RESUME_URL} download className="cursor-magnetic bg-accent text-background px-8 py-4 rounded-full hover:bg-gray-800 transition-all duration-300 font-medium inline-flex items-center gap-3">
                    <Download className="w-5 h-5" />
                    Download Resume
                  </a>
                  <a
                    href="mailto:hello@portfolio.com"
                    className="cursor-magnetic border border-accent text-accent px-8 py-4 rounded-full hover:bg-accent hover:text-background transition-all duration-300 font-medium text-center"
                  >
                    Get In Touch
                  </a>
                </div>
              </div>
              
              <div className="aspect-square bg-muted rounded-lg flex items-center justify-center">
                <span className="text-gray-500">Professional Photo</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section ref={statsRef} className="py-24 border-t border-border">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-4xl mx-auto">
            {stats.map((stat, index) => (
              <div key={index} className="stat-item text-center">
                <div className="bg-muted p-4 rounded-lg mb-4 inline-flex">
                  <stat.icon className="w-8 h-8 text-accent" />
                </div>
                <div className="text-3xl font-bold text-accent mb-2">{stat.value}</div>
                <div className="text-sm text-gray-600">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-24 border-t border-border">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl md:text-5xl font-bold mb-16 text-center">My Story</h2>
            <div className="space-y-8 text-lg text-gray-600 leading-relaxed">
              <p>
                My journey into digital design and development began over 6 years ago with a simple curiosity: 
                how can technology create meaningful human connections? This question has driven every project, 
                every line of code, and every design decision I&apos;ve made since.
              </p>
              <p>
                Starting as a self-taught developer, I quickly discovered that the most impactful digital 
                experiences happen at the intersection of beautiful design and seamless functionality. 
                This realization led me to develop expertise in both creative and technical domains.
              </p>
              <p>
                Today, I specialize in creating award-winning digital experiences that combine cutting-edge 
                animation techniques with thoughtful user experience design. My work has been recognized 
                by industry leaders and has helped clients achieve their business goals through exceptional 
                digital craftsmanship.
              </p>
              <p>
                When I&apos;m not crafting digital experiences, you can find me exploring new technologies, 
                contributing to open-source projects, or sharing knowledge with the design and development community.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Skills Section */}
      <section ref={skillsRef} className="py-24 border-t border-border">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl md:text-5xl font-bold mb-16 text-center">Skills & Expertise</h2>
            <div className="grid md:grid-cols-2 gap-8">
              {skills.map((skillSet, index) => (
                <div key={index} className="skill-category">
                  <div className="flex items-center gap-3 mb-6">
                    <div className="bg-accent text-background p-2 rounded-lg">
                      {skillSet.category === 'Frontend' && <Code className="w-5 h-5" />}
                      {skillSet.category === 'Design' && <Palette className="w-5 h-5" />}
                      {skillSet.category === 'Backend' && <Zap className="w-5 h-5" />}
                      {skillSet.category === 'Tools' && <Award className="w-5 h-5" />}
                    </div>
                    <h3 className="text-xl font-bold">{skillSet.category}</h3>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {skillSet.items.map((skill, skillIndex) => (
                      <span
                        key={skillIndex}
                        className="px-3 py-2 bg-muted rounded-full text-sm font-medium hover:bg-gray-200 transition-colors duration-300"
                      >
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Experience Section */}
      <section ref={experienceRef} className="py-24 border-t border-border">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl md:text-5xl font-bold mb-16 text-center">Experience</h2>
            <div className="space-y-12">
              {experience.map((job, index) => (
                <div key={index} className="experience-item">
                  <div className="border border-border rounded-lg p-8 hover:bg-muted transition-colors duration-300">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-4">
                      <div>
                        <h3 className="text-xl font-bold mb-2">{job.role}</h3>
                        <p className="text-accent font-medium">{job.company}</p>
                      </div>
                      <span className="text-sm text-gray-500 mt-2 md:mt-0">{job.period}</span>
                    </div>
                    <p className="text-gray-600 leading-relaxed">{job.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 border-t border-border">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Let&apos;s Work Together
            </h2>
            <p className="text-lg text-gray-600 mb-8 leading-relaxed">
              I&apos;m always interested in new opportunities and interesting projects. 
              Whether you have a specific idea or just want to explore possibilities, 
              I&apos;d love to hear from you.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/contact"
                className="cursor-magnetic bg-accent text-background px-8 py-4 rounded-full hover:bg-gray-800 transition-all duration-300 font-medium"
              >
                Start a Project
              </a>
              <a
                href="mailto:hello@portfolio.com"
                className="cursor-magnetic border border-accent text-accent px-8 py-4 rounded-full hover:bg-accent hover:text-background transition-all duration-300 font-medium"
              >
                Send Email
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
